import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Send, X, Sparkles, Lock } from "lucide-react";

export default function NoteComposer({ open, target, billing, onClose, onSend }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  if (!open || !target) return null;

  const qplus = billing?.quietplus;
  const credits = billing?.note_credits || 0;
  const canSend = qplus || credits > 0;
  const remaining = qplus ? "∞" : credits;

  const submit = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await onSend(text.trim());
      setText("");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid="note-composer-overlay"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-3xl border border-[#E5DFD3] p-6 sm:p-8 max-w-md w-full"
          data-testid="note-composer"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#913831]">Thoughtful note</div>
              <h3 className="font-serif text-3xl mt-1 text-[#1A1818]">A note to {target.name}</h3>
            </div>
            <button data-testid="note-close" onClick={onClose}
              className="w-9 h-9 rounded-full border border-[#E5DFD3] hover:bg-[#F0EBE1] flex items-center justify-center">
              <X size={16} />
            </button>
          </div>

          <p className="text-sm text-[#737373] mt-3">
            Attach a 180-character note to your like. {target.name} will read it before deciding.
          </p>

          <textarea
            data-testid="note-input"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 180))}
            rows={4}
            disabled={!canSend}
            placeholder={canSend
              ? `Something specific. Reference their bio or a shared interest…`
              : "Out of notes. Upgrade or buy a 5-pack to send one."}
            className="mt-4 w-full bg-[#FAF7F2] border border-[#E5DFD3] rounded-2xl p-4 text-sm focus:outline-none focus:border-[#913831] resize-none disabled:opacity-60"
          />
          <div className="mt-2 flex items-center justify-between text-xs text-[#737373]">
            <span>{text.length}/180</span>
            <span data-testid="note-credits-counter">{qplus ? "Quiet+ · unlimited" : `${remaining} note${remaining === 1 ? "" : "s"} left`}</span>
          </div>

          {!canSend ? (
            <Link to="/billing" data-testid="note-upgrade-btn"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#913831] hover:bg-[#7A2E28] text-white rounded-full py-3 font-medium">
              <Sparkles size={14} /> Get notes
            </Link>
          ) : (
            <button data-testid="note-send-btn" onClick={submit} disabled={sending || !text.trim()}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#913831] hover:bg-[#7A2E28] disabled:opacity-50 text-white rounded-full py-3 font-medium">
              <Send size={14} /> Send note + like
            </button>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
